/**
 * FikoMaterialRecommender - Malzeme Oneri Motoru
 *
 * Secilen uretim prosesine ve parca geometrisine gore
 * en uygun malzeme onerisi yapar.
 *
 * - Proses uyumluluk matrisi kontrolu
 * - Maliyet/performans Pareto optimizasyonu
 * - Minimum mekanik gereksinim kontrolu
 * - Agirlik optimizasyonu secenegi
 */

import {
    FikoMaterialDB,
    getMaterialDB,
    MaterialProperties,
    ManufacturingProcessType,
    MaterialCategory
} from './FikoMaterialDB';

// ============================================================
// RECOMMENDATION TYPES
// ============================================================

export interface MaterialRequirements {
    /** Hedef uretim prosesi */
    targetProcess: ManufacturingProcessType;
    /** Minimum cekme dayanimi (MPa) */
    minTensileStrength?: number;
    /** Minimum akma dayanimi (MPa) */
    minYieldStrength?: number;
    /** Minimum uzama (%) */
    minElongation?: number;
    /** Minimum sertlik (HB) */
    minHardness?: number;
    /** Maksimum yogunluk (kg/cm3) - agirlik optimizasyonu */
    maxDensity?: number;
    /** Maksimum fiyat (USD/kg) */
    maxPricePerKg?: number;
    /** Minimum calisma sicakligi (C) - erime noktasindan dusuk */
    minOperatingTemperature?: number;
    /** Korozyon direnci gerekli mi */
    requireCorrosionResistance?: boolean;
    /** Tercih edilen malzeme kategorileri */
    preferredCategories?: MaterialCategory[];
    /** Minimum darbe dayanimi (J/m) */
    minImpactStrength?: number;
    /** RoHS uyumluluk gerekli mi */
    requireRoHS?: boolean;
    /** Geri donusum gerekli mi */
    requireRecyclable?: boolean;
    /** Agirlik onceliklendirmesi (0-1, 1=en hafif tercih) */
    weightPriority?: number;
    /** Maliyet onceliklendirmesi (0-1, 1=en ucuz tercih) */
    costPriority?: number;
    /** Performans onceliklendirmesi (0-1, 1=en guclu tercih) */
    performancePriority?: number;
}

export interface MaterialRecommendation {
    material: MaterialProperties;
    /** 0-100 uygunluk skoru */
    score: number;
    /** Neden onerildigi */
    reasons: string[];
    /** Turkce sebepler */
    reasonsTR: string[];
    /** Uyari/dezavantajlar */
    warnings: string[];
    warningsTR: string[];
    /** Maliyet/kg (USD) */
    estimatedCostPerKg: number;
    /** Proses uyumluluk skoru */
    processCompatibility: number;
    /** Mekanik uygunluk skoru */
    mechanicalFit: number;
    /** Maliyet skoru (dusuk = iyi) */
    costScore: number;
    /** Agirlik skoru (dusuk = iyi) */
    weightScore: number;
}

export interface RecommendationResult {
    recommendations: MaterialRecommendation[];
    /** Toplam taranan malzeme sayisi */
    totalScanned: number;
    /** Filtreleme sonrasi kalan */
    afterFiltering: number;
    /** Analiz metadatasi */
    analysisNote: string;
    analysisNoteTR: string;
}

// ============================================================
// MATERIAL RECOMMENDER CLASS
// ============================================================

export class FikoMaterialRecommender {
    private materialDB: FikoMaterialDB;

    constructor(materialDB?: FikoMaterialDB) {
        this.materialDB = materialDB || getMaterialDB();
    }

    /**
     * Verilen gereksinimlere gore en uygun malzemeleri oner
     * @param requirements - Malzeme gereksinimleri
     * @param topN - Kac oneri dondurmeli (varsayilan 5)
     */
    recommend(requirements: MaterialRequirements, topN: number = 5): RecommendationResult {
        const allMaterials = this.materialDB.getAllMaterials();
        const totalScanned = allMaterials.length;

        // 1. Proses uyumluluk filtresi
        let candidates = allMaterials.filter(m =>
            m.compatibleProcesses.includes(requirements.targetProcess)
        );

        // 2. Kategori filtresi (varsa)
        if (requirements.preferredCategories && requirements.preferredCategories.length > 0) {
            const catFiltered = candidates.filter(m =>
                requirements.preferredCategories!.includes(m.category)
            );
            // Eger tercih edilen kategoride malzeme yoksa tum adaylari koru
            if (catFiltered.length > 0) {
                candidates = catFiltered;
            }
        }

        // 3. Hard filtreler (eliminasyon)
        candidates = candidates.filter(m => {
            if (requirements.minTensileStrength && m.tensileStrength < requirements.minTensileStrength) return false;
            if (requirements.minYieldStrength && m.yieldStrength < requirements.minYieldStrength) return false;
            if (requirements.minElongation && m.elongation < requirements.minElongation) return false;
            if (requirements.minHardness && m.hardness < requirements.minHardness) return false;
            if (requirements.maxDensity && m.density > requirements.maxDensity) return false;
            if (requirements.maxPricePerKg && m.pricePerKg > requirements.maxPricePerKg) return false;
            if (requirements.minOperatingTemperature && m.meltingPoint < requirements.minOperatingTemperature * 1.5) return false;
            if (requirements.minImpactStrength && (m.impactStrength || 0) < requirements.minImpactStrength) return false;
            if (requirements.requireRoHS && m.roHSCompliant === false) return false;
            if (requirements.requireRecyclable && m.recyclable === false) return false;
            return true;
        });

        const afterFiltering = candidates.length;

        // 4. Skor hesaplama
        const scored: MaterialRecommendation[] = candidates.map(m =>
            this.scoreMaterial(m, requirements)
        );

        // 5. Siralama ve secim
        scored.sort((a, b) => b.score - a.score);
        const recommendations = scored.slice(0, topN);

        return {
            recommendations,
            totalScanned,
            afterFiltering,
            analysisNote: `Scanned ${totalScanned} materials, ${afterFiltering} passed filters, top ${recommendations.length} recommended.`,
            analysisNoteTR: `${totalScanned} malzeme tarandi, ${afterFiltering} tanesi filtreleri gecti, en iyi ${recommendations.length} oneriliyor.`,
        };
    }

    /**
     * Belirli bir prosese uygun tum malzemeleri listele
     */
    getCompatibleMaterials(process: ManufacturingProcessType): MaterialProperties[] {
        return this.materialDB.getMaterialsForProcess(process);
    }

    /**
     * Iki malzemeyi karsilastir
     */
    compareMaterials(id1: string, id2: string): { comparison: Record<string, { mat1: any; mat2: any; winner: string }> } | null {
        const mat1 = this.materialDB.getMaterial(id1);
        const mat2 = this.materialDB.getMaterial(id2);
        if (!mat1 || !mat2) return null;

        const fields: Array<{ key: keyof MaterialProperties; label: string; higherBetter: boolean }> = [
            { key: 'tensileStrength', label: 'Tensile Strength (MPa)', higherBetter: true },
            { key: 'yieldStrength', label: 'Yield Strength (MPa)', higherBetter: true },
            { key: 'elongation', label: 'Elongation (%)', higherBetter: true },
            { key: 'hardness', label: 'Hardness (HB)', higherBetter: true },
            { key: 'density', label: 'Density (kg/cm3)', higherBetter: false },
            { key: 'pricePerKg', label: 'Price ($/kg)', higherBetter: false },
            { key: 'machinabilityIndex', label: 'Machinability', higherBetter: true },
            { key: 'thermalConductivity', label: 'Thermal Conductivity', higherBetter: true },
        ];

        const comparison: Record<string, { mat1: any; mat2: any; winner: string }> = {};
        for (const f of fields) {
            const v1 = mat1[f.key] as number;
            const v2 = mat2[f.key] as number;
            const winner = f.higherBetter
                ? (v1 >= v2 ? mat1.id : mat2.id)
                : (v1 <= v2 ? mat1.id : mat2.id);
            comparison[f.label] = { mat1: v1, mat2: v2, winner };
        }

        return { comparison };
    }

    // ============================================================
    // PRIVATE: SCORING
    // ============================================================

    private scoreMaterial(mat: MaterialProperties, req: MaterialRequirements): MaterialRecommendation {
        const reasons: string[] = [];
        const reasonsTR: string[] = [];
        const warnings: string[] = [];
        const warningsTR: string[] = [];

        // Agirlik oncelikleri
        const weightPriority = req.weightPriority ?? 0.3;
        const costPriority = req.costPriority ?? 0.4;
        const performancePriority = req.performancePriority ?? 0.3;

        // --- Proses uyumluluk skoru ---
        const processCompatibility = mat.compatibleProcesses.includes(req.targetProcess) ? 100 : 0;
        if (processCompatibility === 100) {
            reasons.push(`Compatible with ${req.targetProcess}`);
            reasonsTR.push(`${req.targetProcess} prosesiyle uyumlu`);
        }

        // --- Mekanik uygunluk skoru ---
        let mechanicalFit = 100;
        if (req.minTensileStrength) {
            const ratio = mat.tensileStrength / req.minTensileStrength;
            mechanicalFit = Math.min(mechanicalFit, Math.min(100, ratio * 80));
            if (ratio >= 1.5) {
                reasons.push(`Exceeds tensile strength requirement by ${((ratio - 1) * 100).toFixed(0)}%`);
                reasonsTR.push(`Cekme dayanimi gereksinimi %${((ratio - 1) * 100).toFixed(0)} asiyor`);
            }
        }
        if (req.minYieldStrength) {
            const ratio = mat.yieldStrength / req.minYieldStrength;
            mechanicalFit = Math.min(mechanicalFit, Math.min(100, ratio * 80));
        }
        if (req.minHardness) {
            const ratio = mat.hardness / req.minHardness;
            mechanicalFit = Math.min(mechanicalFit, Math.min(100, ratio * 80));
        }
        if (req.minElongation) {
            const ratio = mat.elongation / req.minElongation;
            mechanicalFit = Math.min(mechanicalFit, Math.min(100, ratio * 80));
        }

        // --- Maliyet skoru (dusuk = iyi) ---
        const allPrices = this.materialDB.getAllMaterials().map(m => m.pricePerKg);
        const maxPrice = Math.max(...allPrices);
        const costScore = maxPrice > 0 ? (1 - mat.pricePerKg / maxPrice) * 100 : 50;

        if (mat.pricePerKg < 5) {
            reasons.push(`Low cost material ($${mat.pricePerKg}/kg)`);
            reasonsTR.push(`Dusuk maliyetli malzeme ($${mat.pricePerKg}/kg)`);
        } else if (mat.pricePerKg > 50) {
            warnings.push(`High cost material ($${mat.pricePerKg}/kg)`);
            warningsTR.push(`Yuksek maliyetli malzeme ($${mat.pricePerKg}/kg)`);
        }

        // --- Agirlik skoru (dusuk = iyi) ---
        const allDensities = this.materialDB.getAllMaterials().map(m => m.density);
        const maxDensity = Math.max(...allDensities);
        const weightScore = maxDensity > 0 ? (1 - mat.density / maxDensity) * 100 : 50;

        if (mat.density < 0.003) {
            reasons.push('Lightweight material');
            reasonsTR.push('Hafif malzeme');
        }

        // --- Korozyon direnci ---
        if (req.requireCorrosionResistance) {
            if (mat.category === 'ferrous_stainless' || mat.category === 'non_ferrous_titanium' || mat.category === 'non_ferrous_nickel') {
                reasons.push('Good corrosion resistance');
                reasonsTR.push('Iyi korozyon direnci');
            } else if (mat.chemicalResistance) {
                const acids = mat.chemicalResistance['acids'];
                if (acids === 'excellent' || acids === 'good') {
                    reasons.push('Good chemical resistance');
                    reasonsTR.push('Iyi kimyasal direnç');
                }
            } else if (mat.category === 'ferrous_steel' || mat.category === 'ferrous_cast_iron') {
                warnings.push('May require surface treatment for corrosion protection');
                warningsTR.push('Korozyon koruması icin yuzey islemi gerekebilir');
            }
        }

        // --- Islenebilirlik ---
        if (mat.machinabilityIndex > 70) {
            reasons.push(`Excellent machinability (${mat.machinabilityIndex}/100)`);
            reasonsTR.push(`Mukemmel islenebilirlik (${mat.machinabilityIndex}/100)`);
        } else if (mat.machinabilityIndex < 20) {
            warnings.push(`Difficult to machine (${mat.machinabilityIndex}/100)`);
            warningsTR.push(`Islemesi zor (${mat.machinabilityIndex}/100)`);
        }

        // --- Toplam skor ---
        const score = Math.round(
            processCompatibility * 0.15 +
            mechanicalFit * performancePriority +
            costScore * costPriority +
            weightScore * weightPriority
        );

        return {
            material: mat,
            score: Math.min(100, Math.max(0, score)),
            reasons,
            reasonsTR,
            warnings,
            warningsTR,
            estimatedCostPerKg: mat.pricePerKg,
            processCompatibility,
            mechanicalFit,
            costScore,
            weightScore,
        };
    }
}

// ============================================================
// SINGLETON
// ============================================================

let _recommender: FikoMaterialRecommender | null = null;

export function getMaterialRecommender(): FikoMaterialRecommender {
    if (!_recommender) {
        _recommender = new FikoMaterialRecommender();
    }
    return _recommender;
}

export function disposeMaterialRecommender(): void {
    _recommender = null;
}
